import { INGLIS_BLUE } from '../../constants/colors'
import { useState } from 'react'
import { useMapContext } from '../../context/MapContext'
import FilterDropdown from './FilterDropdown'
import { FILTER_GROUPS } from '../../constants/filterConfig'
import type { FilterGroupKey, Site } from '../../types'

export default function CombinedBar() {
  const { filters, toggleFilter, clearGroup, resetFilters, isDefaultFilters, selectedSite } = useMapContext()
  const [openKey, setOpenKey] = useState<FilterGroupKey | null>(null)

  // A dropdown left open renders above whatever comes next (it's portaled to <body> with a high
  // z-index so it isn't clipped by scroll containers — see FilterDropdown) — including a site
  // detail card that opens over the same screen region. Closing it on selection avoids that
  // overlap entirely instead of trying to out-stack it.
  const [prevSelectedSite, setPrevSelectedSite] = useState<Site | null>(selectedSite)
  if (prevSelectedSite !== selectedSite) {
    setPrevSelectedSite(selectedSite)
    setOpenKey(null)
  }

  return (
    <div
      // id/tabIndex: target of the "Filters" skip link in App's skip navigation. tabIndex={-1}
      // makes the region programmatically focusable so the link actually moves focus here (a
      // plain id would move only the browser's scroll position and leave the tab sequence back
      // at the top of the page for keyboard and screen-reader users alike).
      id="filter-controls"
      tabIndex={-1}
      role="region"
      aria-label="Filter controls"
      className="flex items-center shrink-0 print:hidden px-4"
      style={{
        background: '#fff', borderBottom: '1px solid #dbe2ea', height: 40, minHeight: 40, gap: 10,
        overflowX: 'auto', WebkitOverflowScrolling: 'touch',
      }}
    >
      <span style={{
        fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '0.07em', color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        Filter:
      </span>

      {FILTER_GROUPS.map(({ key, label, options }) => (
        <FilterDropdown
          key={key}
          label={label}
          filterKey={key}
          options={options}
          filters={filters}
          toggleFilter={toggleFilter}
          clearGroup={clearGroup}
          isOpen={openKey === key}
          onOpen={() => setOpenKey(key)}
          onClose={() => setOpenKey(null)}
        />
      ))}

      <div style={{ flex: 1 }} />

      {!isDefaultFilters && (
        <button
          type="button"
          onClick={resetFilters}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: INGLIS_BLUE, fontWeight: 700, fontSize: '0.82rem',
            whiteSpace: 'nowrap', padding: '0 4px',
          }}
        >
          Reset filters
        </button>
      )}
    </div>
  )
}
